'use strict';

/**
 * Utility functions for handling eating patterns
 */

/**
 * Filters out hidden eating patterns
 * @param {Array} patterns - Array of eating patterns from API
 * @returns {Array} - Array of visible patterns only
 */
export const filterVisiblePatterns = (patterns = []) => {
    return patterns.filter((pattern) => !pattern.hidden);
};

/**
 * Calculates the total time value for an eating pattern's meal types.
 * Uses the sum of all meal begin times as a proxy for "earliness" of the pattern.
 * @param {Array} mealTypes - Array of meal type objects with begin property (minutes from midnight)
 * @returns {number} - Sum of all begin times, or 0 if no valid times
 */
const getTotalMealTime = (mealTypes = []) => {
    return mealTypes.reduce((sum, meal) => {
        return sum + (typeof meal.begin === 'number' ? meal.begin : 0);
    }, 0);
};

/**
 * Sorts eating patterns by:
 * 1. Number of meals (ascending: 2, 3, 4, 5, 6 meals)
 * 2. Within same meal count, by time of day (patterns with earlier meals first)
 *
 * @param {Array} patterns - Array of eating patterns to sort
 * @returns {Array} - New sorted array of patterns (does not mutate original)
 */
export const sortEatingPatterns = (patterns = []) => {
    return [...patterns].sort((a, b) => {
        const aMealCount = a.meal_types?.length || 0;
        const bMealCount = b.meal_types?.length || 0;

        // First, sort by number of meals (ascending)
        if (aMealCount !== bMealCount) {
            return aMealCount - bMealCount;
        }

        // If same meal count, sort by total meal time (earlier patterns first)
        const aTotalTime = getTotalMealTime(a.meal_types);
        const bTotalTime = getTotalMealTime(b.meal_types);

        return aTotalTime - bTotalTime;
    });
};

/**
 * Checks if an eating pattern is available in the list
 * @param {string} patternName - Name of the pattern to check
 * @param {Array} availablePatterns - Array of available patterns
 * @returns {boolean} - True if pattern is available
 */
export const isPatternAvailable = (patternName, availablePatterns = []) => {
    return availablePatterns.some((pattern) => pattern.name === patternName);
};

/**
 * Finds an eating pattern by name
 * @param {string} patternName - Name of the pattern to find
 * @param {Array} availablePatterns - Array of available patterns
 * @returns {Object|null} - Pattern object or null if not found
 */
export const findPatternByName = (patternName, availablePatterns = []) => {
    return availablePatterns.find((pattern) => pattern.name === patternName) || null;
};

/**
 * Artificially includes current pattern in available patterns if it's disabled/hidden
 * This allows users to see their current selection even if it's no longer available
 * @param {string} currentPatternName - Current pattern name
 * @param {Array} allPatterns - All patterns from API (including hidden)
 * @param {Array} availablePatterns - Filtered visible patterns
 * @param {Array} currentMealTypes - Current meal_types from patient preferences (for creating virtual pattern)
 * @returns {Array} - Available patterns with current pattern included if needed
 */
export const includeCurrentPatternIfDisabled = (currentPatternName, allPatterns = [], availablePatterns = [], currentMealTypes = []) => {
    if (!currentPatternName) {
        return availablePatterns;
    }

    // Check if current pattern is already in available patterns
    const isCurrentAvailable = isPatternAvailable(currentPatternName, availablePatterns);

    if (isCurrentAvailable) {
        return availablePatterns;
    }

    // Find current pattern in all patterns (including hidden)
    let currentPattern = findPatternByName(currentPatternName, allPatterns);

    // If not found in allPatterns, create a virtual pattern from currentMealTypes
    // This happens when backend filters out disabled patterns before sending to frontend
    if (!currentPattern && currentMealTypes.length > 0) {
        currentPattern = {
            name: currentPatternName,
            meal_types: currentMealTypes,
            hidden: true, // Mark as hidden since it wasn't in allPatterns
        };
    }

    if (!currentPattern) {
        return availablePatterns;
    }

    // Mark pattern as disabled and include it
    return [{ ...currentPattern, disabled: true }, ...availablePatterns];
};

/**
 * Selects the appropriate eating pattern based on current preferences
 * @param {Object} options - Configuration options
 * @param {Array} options.allPatterns - All eating patterns from API response
 * @param {string} options.currentPatternName - Currently selected pattern name
 * @param {string} options.fallbackPatternName - Fallback pattern from API resolve
 * @param {boolean} options.includeDisabledCurrent - If true, includes current pattern even if disabled
 * @param {Array} options.currentMealTypes - Current meal_types from patient preferences (for creating virtual pattern)
 * @returns {Object} - Selected pattern information
 * @returns {string} result.selectedPatternName - Name of selected pattern
 * @returns {Array} result.selectedMealTypes - Meal types for selected pattern
 * @returns {Array} result.availablePatterns - All visible patterns (for dropdown)
 * @returns {boolean} result.isCurrentPatternDisabled - True if selected pattern is disabled
 * @throws {Error} - If no patterns are available
 */
export const selectEatingPattern = ({
    allPatterns = [],
    currentPatternName = null,
    fallbackPatternName = null,
    includeDisabledCurrent = false,
    currentMealTypes = [],
}) => {
    // Filter out hidden patterns and sort by meal count then time of day
    let availablePatterns = sortEatingPatterns(filterVisiblePatterns(allPatterns));

    // If includeDisabledCurrent is true, artificially add current pattern if it's disabled
    if (includeDisabledCurrent) {
        availablePatterns = includeCurrentPatternIfDisabled(currentPatternName, allPatterns, availablePatterns, currentMealTypes);
    }

    // Edge case: no patterns available
    if (availablePatterns.length === 0) {
        throw new Error('No eating patterns available for this nutrition pattern');
    }

    // Try to use current pattern name first
    const patternNameToCheck = currentPatternName || fallbackPatternName;

    let selectedPattern;
    let isCurrentPatternDisabled = false;

    if (patternNameToCheck && isPatternAvailable(patternNameToCheck, availablePatterns)) {
        // Current pattern is available, use it
        selectedPattern = findPatternByName(patternNameToCheck, availablePatterns);
        isCurrentPatternDisabled = selectedPattern.disabled === true;
    } else {
        // Current pattern not available, use first visible one
        selectedPattern = availablePatterns[0];
    }

    return {
        selectedPatternName: selectedPattern.name,
        selectedMealTypes: selectedPattern.meal_types,
        availablePatterns,
        isCurrentPatternDisabled,
    };
};

/**
 * Creates options array for dropdown from eating patterns
 * @param {Array} patterns - Array of eating patterns
 * @param {Function} labelFormatter - Function to format the label (receives meal_types)
 * @returns {Array} - Array of {value, label} objects for Select component
 */
export const createPatternOptions = (patterns = [], labelFormatter = null) => {
    return patterns.map((pattern) => ({
        value: pattern.name,
        label: labelFormatter ? labelFormatter(pattern.meal_types) : pattern.name,
    }));
};
